import { FC, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import SecondaryButton from '~src/shared/buttons/basicButtons/SecondaryButton.sc';
import PrimaryButtonLoading from '~src/shared/buttons/loadingButtons/PrimaryButtonLoading';
import CustomFormInput from '~src/shared/form/inputs/FormInput';
import { SubObjectiveItem } from '~src/store/okrs/interfaces';
import { FieldType } from '~src/types';
import {
  ButtonSection,
  FormContainer,
  FormInner,
  FormSectionWrapper,
} from '~src/modules/okrs/manageObjectives/create/addObjectiveEntityForm.sc';
import { Title } from '~src/modules/okrs/manageObjectives/edit/editListItem.sc';
import { translateContent } from '~src/services/translationServices';

interface Props {
  hide: () => void;
  subobjective: SubObjectiveItem;

  changeWeight(subobjectiveId: string, weighting: number | null): void;
}

interface FormValues {
  weighting: number | null;
}

const EditSubobjectiveForm: FC<Props> = props => {
  const [t] = useTranslation();

  const submitChangeWeight = (data: FormValues) => {
    props.changeWeight(props.subobjective.id, data.weighting);
  };

  const renderInput = useCallback(
    ({ autoFocus, input, label, meta: { touched, error }, labelKey }: FieldType) => (
      <CustomFormInput
        {...input}
        autoFocus={autoFocus}
        error={touched ? error : null}
        label={label}
        type={input.type}
        labelKey={labelKey}
      />
    ),
    [],
  );

  const weightingValueMapper = (value: string) =>
    value === '' || value === null ? null : Math.min(parseInt(value, 10), 100);

  return (
    <Form
      initialValues={{
        weighting:
          props.subobjective && props.subobjective.weighting_locked ? props.subobjective.weighting : null,
      }}
      onSubmit={submitChangeWeight}
      destroyOnUnregister
    >
      {({ handleSubmit }) => (
        <FormContainer onSubmit={handleSubmit}>
          <Title>{translateContent(props.subobjective.name)}</Title>
          <FormSectionWrapper>
            <FormInner>
              <Field
                autoFocus
                name="weighting"
                label={t('x.okr.add.weighting')}
                onBlur={blur}
                type="number"
                children={renderInput}
                parse={weightingValueMapper}
              />
            </FormInner>
            <ButtonSection>
              <SecondaryButton
                type="button"
                onClick={props.hide}
                data-translation="x.okr.labels.button.cancel"
              >
                {t('x.okr.labels.button.cancel')}
              </SecondaryButton>

              <PrimaryButtonLoading
                isLoading={false}
                data-translation="x.okr.sub-objective.save.title"
              >
                {t('x.okr.sub-objective.save.title')}
              </PrimaryButtonLoading>
            </ButtonSection>
          </FormSectionWrapper>
        </FormContainer>
      )}
    </Form>
  );
};

export default EditSubobjectiveForm;
